import isCategoryInMasterList from "owa-categories/lib/utils/isCategoryInMasterList";
import { lazySubscribeToCategoryNotifications } from "owa-categories";
import { logUsage, logGreyError } from "owa-analytics";
import type { FavoriteData, FavoriteCategoryData, FavoriteFolderData, FavoriteGroupData, OutlookFavoriteKind, OutlookFavoriteServiceDataType, } from "owa-favorites-types";
import { FolderTreeLoadStateEnum } from "owa-folders";
import getUserConfiguration from "owa-session-store/lib/actions/getUserConfiguration";
import getFavorites from "owa-session-store/lib/utils/getFavorites";
import { type FavoritesBitFlagsMasks, getIsBitEnabled } from "../actions/helpers/favoritesBitFlagsActions";
import { setFavoritesLoaded } from "../actions/setFavoritesLoaded";
import onFavoritesLoaded from "../actions/onFavoritesLoaded";
import { addFavoriteToStoreInitialMutator } from "../mutators/addFavoriteMutators";
import { addToOrderedFavoriteIdList } from "../actions/v2/addToOrderedFavoriteIdList";
import { isGroupInFavorites } from "../selectors/isInFavorites";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list";
import { lazyAddNewMailboxDefaultFavorites, lazySetOutlookFavoritesBitFlag, lazySubscribeToFavoriteNotifications, } from "../lazyFunctions";
import getOutlookFavoritesService from "../services/v2/getOutlookFavoritesService";
import favoritesStore from "../store/store";
import { convertServiceResponseToFavoriteData } from "../utils/favoriteServiceDataUtils";
import type { MailboxInfo, AccountKey } from "owa-client-types";
import { initializeFavoriteTreeData } from "./initializeFavoriteTreeData";
import { getAccountKeyForMailboxInfo, isSameCoprincipalAccountByMailboxAndAccountKey, } from "owa-client-types";
import getCombinedFavoritesFromCache from "../utils/getCombinedFavoritesFromCache";
import setCombinedFavoritesInCache from "../utils/setCombinedFavoritesInCache";
import { isCapabilitySupported } from "owa-capabilities";
import { favoritesRoamingCapability } from "owa-capabilities-definitions/lib/favoritesRoamingCapability";
import { setFolderFavoritesDataCollectionInformation } from "./registerDataCollector";
import { setLoadingStateForFavorites } from "../actions/setLoadingStateForFavorites";
import { setCombinedFavoriteIdsInStoreMutator } from "../mutators/setCombinedFavoriteIdsInStoreMutator";
import { getOutlookFavoriteTypeFromMailboxInfo } from "./getUniqueDataForFavoriteType";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { getCoprincipalAccountForMailboxInfo } from "owa-account-source-list-store";
import onFavoriteParsingComplete from "../actions/onFavoriteParsingComplete";

const HOUR_TIMESPAN_IN_MS = 1000 * 60 * 60;

/**
 * Load outlook favorites
 */
export default async function loadOutlookFavorites(mailboxInfo: MailboxInfo): Promise<void> {
    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
        // For Unified Inbox, we don't load outlook favorites
        // as it is not supported in Unified Inbox
        return;
    }
    
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    
    initializeFavoriteTreeData(accountKey);
    
    const loadingState = favoritesStore()?.favoriteTreeData.get(accountKey)?.loadingState;
    if (loadingState === FolderTreeLoadStateEnum.Loading ||
        loadingState == FolderTreeLoadStateEnum.Loaded) {
        return;
    }
    
    setLoadingStateForFavorites(accountKey, FolderTreeLoadStateEnum.Loading);
    
    // Load UserConfiguration.Favorites if there is any
    // We want to load the Favorites to store first and then migrate, because migrateOutlookFavorites
    // will send multiple create service call and await for all of them succeeds, and we don't want to delay boot
    await loadOutlookFavoritesToStore(mailboxInfo);
    
    const firstRunCompleted = getIsBitEnabled(2);
    // Add default favorites for new users
    if (!firstRunCompleted) {
        // Only add default folders for new outlook users
        if (favoritesStore()?.favoriteTreeData.get(accountKey)?.orderedOutlookFavoritesIds.length ==
            0 &&
            isNewUser()) {
            lazyAddNewMailboxDefaultFavorites.importAndExecute(mailboxInfo);
        }
        lazySetOutlookFavoritesBitFlag.importAndExecute(true, 2);
    }
    logLoadOutlookFavoritesDatapoints(mailboxInfo);
    
    setFavoritesLoaded(accountKey);
    onFavoritesLoaded(accountKey);
    
    lazySubscribeToFavoriteNotifications.importAndExecute(mailboxInfo);
}

async function loadOutlookFavoritesToStore(mailboxInfo: MailboxInfo) {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    
    // Use session data for logged in email address even when reload is true.
    // This is to avoid a service call when the data is available in the session store.
    let outlookFavorites = getFavorites(mailboxInfo)?.value;
    if (outlookFavorites) {
        logUsage("FP_UserConfigFavoritesLoaded", mailboxInfo, {
            favoriteCount: outlookFavorites.length,
        });
    }
    
    if (!outlookFavorites && isCapabilitySupported(favoritesRoamingCapability, mailboxInfo)) {
        // If there are no favorites in session data, retry using OWS' call
        let outlookFavoritesResponse;
        
        try {
            outlookFavoritesResponse = await getOutlookFavoritesService(mailboxInfo);
        }
        catch (error) {
            logLoadOutlookFavoritesRetryFailed(mailboxInfo);
            setFolderFavoritesDataCollectionInformation(accountKey + " :GetOutlookFavoritesRetryFailed", error instanceof Error ? error.message : "UnknownError");
            return;
        }
        
        outlookFavorites = outlookFavoritesResponse?.value;
        
        if (!outlookFavorites) {
            return;
        }
        
        logUsage("FavoritesRetrySuccess", mailboxInfo);
    }
    setFolderFavoritesDataCollectionInformation(accountKey + ":FavoritesFromService", JSON.stringify(outlookFavorites));
    
    if (outlookFavorites) {
        const favoriteIds: string[] = [];
        let currentFavoriteId = undefined;
        let loadFavoritesFailureMessage = "";
        let loadFavoritesFailureCount = 0;
        // Populate favorites based on the OutlookFavorites
        for (const serviceDataIterator of outlookFavorites) {
            if (!serviceDataIterator) {
                continue;
            }
            const serviceData = serviceDataIterator as OutlookFavoriteServiceDataType;
            if (serviceData && serviceData.Id) {
                currentFavoriteId = serviceData.Id;
                const favoriteType = serviceData.Type?.toLocaleLowerCase() as OutlookFavoriteKind;
                try {
                    const favoriteData = convertServiceResponseToFavoriteData(serviceData, favoriteType, mailboxInfo);
                    if (!favoriteData) {
                        setFolderFavoritesDataCollectionInformation(accountKey + ":FavoriteDataConversionFailed: " + currentFavoriteId, "FavoriteDataConversionFailed");
                        return;
                    }
                    favoriteIds.push(favoriteData.favoriteId);
                    
                    switch (favoriteData.type?.toLocaleLowerCase()) {
                        case "folder":
                            const favoriteFolderData = favoriteData as FavoriteFolderData;
                            if (favoriteFolderData.isSearchFolder) {
                                addFavoriteFolderToStore(favoriteData, false /*initialIsVisible*/);
                            }
                            else {
                                tryAddSingleFavoriteFolderToStore(favoriteData as FavoriteFolderData, serviceData.SingleValueSettings?.[0].Value ?? "");
                            }
                            
                            break;
                        
                        case "persona":
                            addFavoriteFolderToStore(favoriteData);
                            break;
                        
                        case "publicfolder":
                            addFavoriteFolderToStore(favoriteData);
                            break;
                        
                        case "group":
                            tryAddSingleFavoriteGroupToStore(favoriteData as FavoriteGroupData);
                            break;
                        
                        case "category":
                            tryAddSingleFavoriteCategoryToStore(favoriteData as FavoriteCategoryData, mailboxInfo);
                            break;
                        
                        case "news":
                        case "privatedistributionlist":
                            // News and Private Distribution List favorites are not supported in OWA
                            break;
                        
                        default:
                            // VSO 26085: Optimize updating the favorite position data
                            // For unknown type, we have to update orderedOutlookFavoritesIds in order to
                            // record the index, which is used to locally update the favorite store
                            addToOrderedFavoriteIdList(favoriteData);
                            loadFavoritesFailureMessage +=
                                getOutlookLoadFavoriteData(favoriteData) + "; ";
                            loadFavoritesFailureCount++;
                            setFolderFavoritesDataCollectionInformation(accountKey + ":AddingUnknownFavoriteType: ", favoriteData.type);
                            break;
                    }
                }
                catch (error) {
                    // continue execution if a favorite could not be loaded
                    setFolderFavoritesDataCollectionInformation(accountKey + ":AddFavoriteToStoreInitialFailed: " + currentFavoriteId, 
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS18046: 'error' is of type 'unknown'.
                    // @ts-expect-error
                    error.toString());
                }
            }
        }
        if (loadFavoritesFailureCount > 0) {
            logGreyError("AddingUnknownFavoriteType", mailboxInfo, undefined, {
                errorMessage: loadFavoritesFailureMessage,
                errorCount: loadFavoritesFailureCount,
            });
        }
        setCombinedFavoritesInStoreAndCache(favoriteIds, accountKey, mailboxInfo);
        if (favoriteIds.length > 0) {
            onFavoriteParsingComplete(favoriteIds);
        }
    }
}

/**
 * Set the combined favorites including local ones in cache and store
 * @param combinedFavorites the favorite folder data to be added
 */
function setCombinedFavoritesInStoreAndCache(favoriteIds: string[], accountKey: AccountKey, mailboxInfo: MailboxInfo) {
    // remove any deleted favorites from cache, if combined favorites flight is on
    if (isMonarchMultipleAccountsEnabled()) {
        const cachedFavoritesList = getCombinedFavoritesFromCache();
        const validLocalFolderFavorites: string[] = [];
        
        setFolderFavoritesDataCollectionInformation(accountKey + ":cachedFavoritesList", JSON.stringify(cachedFavoritesList));
        
        if (cachedFavoritesList) {
            const validFavorites = cachedFavoritesList.combinedFavorites.filter(favoriteFromCache => {
                if (!favoriteFromCache ||
                    !favoriteFromCache.mailboxInfo ||
                    getCoprincipalAccountForMailboxInfo(favoriteFromCache.mailboxInfo) ===
                        undefined) {
                    return false;
                }
                
                const isSameAccount = isSameCoprincipalAccountByMailboxAndAccountKey(favoriteFromCache.mailboxInfo, accountKey);
                
                return (!isSameAccount ||
                    (isSameAccount && favoriteIds.indexOf(favoriteFromCache.favoriteId) >= 0) ||
                    (isMonarchMultipleAccountsEnabled() &&
                        favoriteFromCache.type?.startsWith("localFolder")));
            });
            
            // This is needed for local favorite folders, as they are not in the favorites list returned from the service
            if (isMonarchMultipleAccountsEnabled()) {
                for (let i = 0; i < validFavorites.length; i++) {
                    const favoriteFromCache = validFavorites[i];
                    if (favoriteFromCache.type?.startsWith("localFolder")) {
                        const favoriteData: FavoriteFolderData = {
                            favoriteId: favoriteFromCache.favoriteId,
                            mailboxInfo: favoriteFromCache.mailboxInfo,
                            folderId: favoriteFromCache.uniqueId ?? "",
                            isSearchFolder: false,
                            treeType: "favorites",
                            type: getOutlookFavoriteTypeFromMailboxInfo(favoriteFromCache.mailboxInfo),
                            displayName: favoriteFromCache.displayName ?? "",
                            client: "OWA",
                        };
                        
                        validLocalFolderFavorites.push(favoriteFromCache.favoriteId);
                        addFavoriteFolderToStore(favoriteData);
                    }
                }
            }
            
            if (validFavorites) {
                onFavoriteParsingComplete(validLocalFolderFavorites); // Need to call this for local folder favorites added from cache
                setCombinedFavoriteIdsInStoreMutator(validFavorites.map(favorite => favorite.favoriteId));
                setCombinedFavoritesInCache({ combinedFavorites: validFavorites });
                setFolderFavoritesDataCollectionInformation(accountKey + ":validFavorites", JSON.stringify(validFavorites));
            }
        }
        
        logUsage("FP_OfflineFavoritesCache", mailboxInfo, {
            hit: !!cachedFavoritesList,
            cachedCount: cachedFavoritesList?.combinedFavorites.length ?? 0,
            localFolderCount: validLocalFolderFavorites.length,
        });
    }
}

/**
 * Add single favorite folder function to avoid eslint-disable-next-line owa-custom-rules/forbid-mutators-inside-loops
 * Part one of refactoring of outlookFavorites package to be clean of eslint errors, next checkins will clean these up
 * @param favoriteData the favorite folder data to be added
 * @param isInitialVisible indicates whether the favorite is initially visible
 */
function addFavoriteFolderToStore(favoriteData: FavoriteData, isInitialVisible?: boolean) {
    addFavoriteToStoreInitialMutator(favoriteData, isInitialVisible /*initialIsVisible*/);
}

/**
 * Add single favorite folder
 * @param favoriteData the favorite folder data to be added
 * @param restFolderId the folderId to be added
 */
function tryAddSingleFavoriteFolderToStore(favoriteData: FavoriteFolderData, restFolderId: string) {
    if (!favoritesStore().outlookFavorites.has(restFolderId)) {
        // Add the folders if the folder is not in favorites yet. This could happen during firstRun, if user already has the default favorite folder.
        // We don't check to see if the folder exists here, because it could just not be loaded yet. We try to fetch unloaded folders in onOutlookFavoritesV2Loaded
        addFavoriteToStoreInitialMutator(favoriteData, false /*initialIsVisible*/);
    }
    else {
        setFolderFavoritesDataCollectionInformation(favoriteData.favoriteId, "tryAddSingleFavoriteFolderToStore_Failed");
    }
}

/**
 * Add single favorite category
 * @param favoriteData the favorite category data to be added
 * @param mailboxInfo the mailbox info of the favorite category
 */
function tryAddSingleFavoriteCategoryToStore(favoriteData: FavoriteCategoryData, mailboxInfo: MailboxInfo) {
    if (shouldAddFavoriteCategory(favoriteData, mailboxInfo)) {
        addFavoriteToStoreInitialMutator(favoriteData);
        lazySubscribeToCategoryNotifications.importAndExecute(mailboxInfo); // This call makes sure that we only subscribe once, still need it here because this could be the first fav category added.
    }
    else {
        setFolderFavoritesDataCollectionInformation(favoriteData.favoriteId, "tryAddSingleFavoriteCategoryToStore_Failed ");
    }
}

/**
 * Should add folder to favorite list
 * @param favoriteData the favorite category data
 * @param mailboxInfo the mailbox info of the favorite category
 * @return a boolean which indicates whether we should add a favorite category
 */
function shouldAddFavoriteCategory(favoriteData: FavoriteCategoryData, mailboxInfo: MailboxInfo): boolean {
    // Add the category to favorites if it is still valid and it is not favorited
    // Otherwise skip adding it, e.g the category has been deleted by the user from other client
    return (isCategoryInMasterList(favoriteData.categoryId, mailboxInfo) &&
        !favoritesStore().outlookFavorites.has(favoriteData.favoriteId));
}

/**
 * Add single favorite group
 * @param favoriteData the favorite group data to be added
 */
function tryAddSingleFavoriteGroupToStore(favoriteData: FavoriteGroupData) {
    if (!isGroupInFavorites(favoriteData.groupId)) {
        addFavoriteToStoreInitialMutator(favoriteData);
    }
    else {
        setFolderFavoritesDataCollectionInformation(favoriteData.favoriteId, "tryAddSingleFavoriteGroupToStore_Failed ");
    }
}

function logLoadOutlookFavoritesDatapoints(mailboxInfo: MailboxInfo) {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const allFavorites = [...favoritesStore().outlookFavorites.values()];
    
    const outlookFavoriteFolders = allFavorites.filter(favorite => favorite.type === "folder");
    
    const migratedFolders = outlookFavoriteFolders.filter(favorite => favorite.client === "Migration");
    
    const outlookFavoriteCategories = allFavorites.filter(favorite => favorite.type === "category");
    
    const migratedCategories = outlookFavoriteCategories.filter(favorite => favorite.client == "Migration");
    
    const outlookFavoritePersonas = allFavorites.filter(favorite => favorite.type === "persona");
    
    const migratedPersonas = outlookFavoritePersonas.filter(favorite => favorite.client == "Migration");
    
    const outlookFavoriteGroups = allFavorites.filter(favorite => favorite.type === "group");
    
    const outlookFavoriteLocalFolders = allFavorites.filter(favorite => favorite.type?.startsWith("localFolder"));
    
    logUsage("LoadOutlookFavorites", mailboxInfo, [
        outlookFavoriteFolders.length,
        outlookFavoriteGroups.length,
        outlookFavoriteCategories.length,
        outlookFavoritePersonas.length,
        migratedFolders.length,
        migratedCategories.length,
        migratedPersonas.length,
        outlookFavoriteLocalFolders.length,
    ]);
    
    // Add the favorites id list to the data collector
    const orderedOutlookFavoritesIds = isMonarchMultipleAccountsEnabled()
        ? favoritesStore().orderedCombinedOutlookFavoritesIds
        : favoritesStore().favoriteTreeData.get(accountKey)?.orderedOutlookFavoritesIds;
    setFolderFavoritesDataCollectionInformation(accountKey + ":orderedFavoriteIdList", JSON.stringify(orderedOutlookFavoritesIds));
}

// Returns true if the user's mailbox has been created within the last hour
function isNewUser() {
    const createDate = new Date(getUserConfiguration()?.MailboxCreateDate ?? Date.now());
    return Date.now() - createDate.getTime() < HOUR_TIMESPAN_IN_MS;
}

// Log info for unknown favorite type that's scrubbed of any PII data
function getOutlookLoadFavoriteData(favoriteData: FavoriteData) {
    const favoriteIdLogString = favoriteData?.favoriteId && favoriteData.favoriteId.length > 0
        ? "favoriteIdPresent"
        : "favoriteIdAbsent";
    const favoriteDisplayNameLogString = favoriteData?.displayName && favoriteData.displayName.length > 0
        ? "favoriteDisplayNamePresent"
        : "favoriteDisplayNameAbsent";
    return ("AddingUnknownFavoriteType " +
        " type: " +
        favoriteData?.type?.toString() +
        " client: " +
        favoriteData?.client?.toString() +
        " favoriteId: " +
        favoriteIdLogString +
        " favoriteDisplayName: " +
        favoriteDisplayNameLogString);
}

function logLoadOutlookFavoritesRetryFailed(mailboxInfo: MailboxInfo) {
    // Part 2 of Favorites eslint removals, plan to fix forbid-specific-functions-on-catch-finally-blocks in next checkins
    logUsage("GetOutlookFavoritesRetryFailed", mailboxInfo);
}
